/**
 * THE LOUD OVERRIDE (Tom, 2026-10-26: overrides stay, and are made loud;
 * before the merge). A deliberate override hides links: every link the
 * holder's references make into the island at another version. Those whose
 * records are MISSING at the kept version are BROKEN — the links are gone
 * from the project, and calculations through them are incomplete. A record
 * that only changed gets nothing beyond the ordinary override notice.
 *
 * VC-D, vc-d/v33 7b67830b4c: on each reference-pins listing entry (the
 * override card) and on a settled override's `pin_conflicts` entry (the
 * alert), all 0 / [] / 0 unless it drops something. Projects the reader
 * cannot read are counted, never named. The kept version is the pin's
 * `selected` (the alert's `chosen`).
 */

export const OVERRIDE_WIRE = {
  droppedField: "dropped_links",
  brokenField: "broken_links",
  hiddenProjectsField: "hidden_projects",
  /** a report's `incomplete_json.missing[]` entry, its sentences in `details` */
  reportMissingCode: "override_drops_record",
} as const;

/** One linking project's links into records missing at the kept version,
 *  at one route version (`align_to`: aligning there restores them). */
export type BrokenLinks = {
  /** null: a project the reader cannot read */
  project: { id: string; name?: string | null } | null;
  align_to: string;
  links: number;
  /** the records' names the reader may read (at the version where they exist) */
  records: string[];
  hidden_records: number;
};

export type OverrideDrops = { dropped: number; broken: BrokenLinks[]; hiddenProjects: number };

const count = (v: unknown) => (typeof v === "number" && v > 0 ? Math.floor(v) : 0);

function broken(v: unknown): BrokenLinks | null {
  const b = v as Record<string, unknown>;
  if (!b || typeof b !== "object" || typeof b.align_to !== "string" || !b.align_to) return null;
  const p = b.project as { id?: unknown; name?: unknown } | null;
  return {
    project: p && typeof p === "object" && typeof p.id === "string" ? { id: p.id, name: typeof p.name === "string" ? p.name : null } : null,
    align_to: b.align_to,
    links: count(b.links),
    records: Array.isArray(b.records) ? b.records.filter((r): r is string => typeof r === "string" && !!r) : [],
    hidden_records: count(b.hidden_records),
  };
}

/** What an override drops, from a listing entry or an alert entry; null when
 *  it drops nothing. */
export function overrideDropsOf(entry: unknown): OverrideDrops | null {
  if (!entry || typeof entry !== "object") return null;
  const e = entry as Record<string, unknown>;
  const list = Array.isArray(e[OVERRIDE_WIRE.brokenField]) ? (e[OVERRIDE_WIRE.brokenField] as unknown[]).map(broken).filter((b): b is BrokenLinks => !!b) : [];
  const drops = { dropped: count(e[OVERRIDE_WIRE.droppedField]), broken: list, hiddenProjects: count(e[OVERRIDE_WIRE.hiddenProjectsField]) };
  return drops.dropped || drops.broken.length ? drops : null;
}
